import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import {
  useCreateSkillSubCategory,
  useUpdateSkillSubCategory,
} from '../../hooks/use-skill-sub-categories';
import {
  skillSubCategoryFormSchema,
  SkillSubCategoryFormValues,
} from '../../schemas/skill-sub-category-form-schema';
import { SkillSubCategory } from '../../types/skill-sub-category.types';

export interface SkillSubCategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skillSubCategory?: SkillSubCategory | null;
}

export const SkillSubCategoryFormDialog = ({
  open,
  onOpenChange,
  skillSubCategory,
}: SkillSubCategoryFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(skillSubCategory);

  const { mutateAsync: createSkillSubCategory, isPending: isCreating } =
    useCreateSkillSubCategory();
  const { mutateAsync: updateSkillSubCategory, isPending: isUpdating } =
    useUpdateSkillSubCategory();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<SkillSubCategoryFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(skillSubCategoryFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Title: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Title: skillSubCategory?.Title ?? '' });
  }, [open, skillSubCategory, form]);

  const handleSubmit = async (values: SkillSubCategoryFormValues) => {
    try {
      if (isEditMode && skillSubCategory) {
        await updateSkillSubCategory({ itemId: skillSubCategory.ItemId, input: values });
      } else {
        await createSkillSubCategory(values);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('EDIT_SKILL_SUB_CATEGORY') : t('CREATE_SKILL_SUB_CATEGORY')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="Title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('TITLE')} *</FormLabel>
                  <FormControl>
                    <Input placeholder={t('TITLE')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
